import { useMemo } from "react";

import { getSelectorsByUserAgent } from "react-device-detect";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    experiments as experimentManifest,
    getExperimentWithExposures
} from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import {
    JourneyLocalStorage,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";

import { useConfirmationPage } from "./use-confirmation-page";

export const userRole = {
    n2n: "userRole.n2n"
};

export const queryParam = {
    templateId: "queryParam.templateId",
    templateChoice: "queryParam.templateChoice",
    products: "queryParam.products",
    teamType: "queryParam.team_type",
    jtbd: "queryParam.jtbd"
};

export const localStorageData = {
    team: "localStorage.team",
    persona: "localStorage.persona"
};

export const experiment = {
    test: {
        cohort: `experiment.test.cohort`,
        enrolled: `experiment.test.enrolled`,
        value: `experiment.test.value`
    },
    d0JtbdOnboarding: {
        cohort: `experiment.d0JtbdOnboarding.cohort`
    }
};

export const featureGate = {
    jiraOnboardingAiMadlib: "featureGate.jiraOnboardingAiMadlib"
};

export const user = {
    isExistingJiraUser: "user.isExistingJiraUser",
    isExistingJsmUser: "user.isExistingJsmUser",
    isN2n: "user.isN2n",
    isMobile: "user.isMobile"
};

export type Metadata =
    | string
    | string[]
    | boolean
    | boolean[]
    | number[]
    | undefined;

export type RegisteredMetadata = Map<string, Metadata> | undefined;

/**
 * Custom hook to return a key value pair of query params and experiment enrollment data.
 * @returns @type {RegisteredMetadata}
 */
export const useRegisterMetadata = (): RegisteredMetadata => {
    const {
        templateId,
        templateChoice: jiraTemplateChoiceOption,
        teamType,
        jtbd,
        products = []
    } = useSearchParameters();
    const { experiments = [], availableProducts } = useConfirmationPage();
    const productList = products.map((product) => product);

    // Gather anchor local storage data
    let localStorageTeam: string | undefined;
    let localStoragePersona: string | undefined;

    const { getAnchorLocalStorage } = useAnchorLocalStorage();
    const anchorLocalStorage = getAnchorLocalStorage();
    if (anchorLocalStorage) {
        try {
            const localStorageObj: JourneyLocalStorage =
                JSON.parse(anchorLocalStorage);
            localStorageTeam = localStorageObj.team;
            localStoragePersona = localStorageObj.persona;
        } catch (e) {
            console.log(e);
        }
    }

    const userAgent = navigator.userAgent;
    const { isMobile } = getSelectorsByUserAgent(userAgent) ?? {
        isMobile: false
    };

    // eslint-disable-next-line complexity
    const metadata = useMemo(() => {
        const map = new Map<
            string,
            string | boolean | string[] | boolean[] | number[]
        >();
        if (templateId) {
            map.set(queryParam.templateId, templateId);
        }

        if (jiraTemplateChoiceOption) {
            map.set(queryParam.templateChoice, jiraTemplateChoiceOption);
        }

        if (productList) {
            map.set(queryParam.products, productList);
        }

        if (teamType) {
            map.set(queryParam.teamType, teamType);
        }

        if (jtbd) {
            map.set(queryParam.jtbd, jtbd);
        }

        if (localStorageTeam) {
            map.set(localStorageData.team, localStorageTeam);
        }

        if (localStoragePersona) {
            map.set(localStorageData.persona, localStoragePersona);
        }

        if (availableProducts?.sites) {
            if (availableProducts?.sites.length === 0) {
                map.set(user.isN2n, true);
            } else {
                map.set(user.isN2n, false);
            }

            const jiraSites =
                availableProducts?.sites?.filter((site) =>
                    site.products.includes("jira-software.ondemand")
                ) ?? [];

            if (jiraSites.length > 0) {
                map.set(user.isExistingJiraUser, true);
            } else {
                map.set(user.isExistingJiraUser, false);
            }

            const jsmSites =
                availableProducts?.sites?.filter((site) =>
                    site.products.includes("jira-servicedesk.ondemand")
                ) ?? [];

            if (jsmSites.length > 0) {
                map.set(user.isExistingJsmUser, true);
            } else {
                map.set(user.isExistingJsmUser, false);
            }
        } else {
            map.set(user.isExistingJiraUser, false);
            map.set(user.isExistingJsmUser, false);
            map.set(user.isN2n, true);
        }

        for (const [key, value] of experiments) {
            if (value.enrollmentPreconditionsMet) {
                map.set(`experiment.${key}.cohort`, value.cohort);
            }

            map.set(
                `experiment.${key}.enrolled`,
                value.enrollmentPreconditionsMet
            );
        }

        const d0JtbdExperimentStatus = getExperimentWithExposures(
            experimentManifest.twcg990D0JTBDOnboarding
        );

        if (
            d0JtbdExperimentStatus.type === "enrolled" &&
            !d0JtbdExperimentStatus.usingDefault
        ) {
            map.set(
                experiment.d0JtbdOnboarding.cohort,
                d0JtbdExperimentStatus.value
            );
        } else {
            map.set(experiment.d0JtbdOnboarding.cohort, "not-enrolled");
        }

        if (
            checkFeatureFlagGate(featureFlags.jiraOnboardingAiMadlib) &&
            productList.includes("jira-software.ondemand") &&
            productList.length === 1
        ) {
            map.set(featureGate.jiraOnboardingAiMadlib, true);
        }

        map.set(user.isMobile, isMobile);

        return map;
    }, [
        templateId,
        productList,
        experiments,
        teamType,
        localStorageTeam,
        localStoragePersona,
        jtbd
    ]);

    return metadata;
};
